import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { stateGuard } from '../shared/guards/state.guard';
import { authGuard } from '../shared/guards/auth.guard';
import { auditTrailPrintGuard } from '../shared/guards/audit-trail-print.guard';
import { PrintViewComponent } from '../shared/components/print-view/print-view.component';
import { AssaySummaryComponent } from './assay-analysis-module/assay-summary/assay-summary.component';
import { CalibrationDetailsComponent } from './calibration-module/calibration-details/calibration-details.component';
import { AssayDetailsFailuresComponent } from './assay-analysis-module/assay-details-failures/assay-details-failures.component';
import { AssayFailuresComponent } from './assay-analysis-module/assay-failures/assay-failures.component';
import { AboutComponent } from './about/about.component';
import { AuditTrailComponent } from './about/audit-trail/audit-trail.component';
import { SampleErrorsComponent } from './sample-errors-module/sample-errors/sample-errors.component';
import { RunStatusComponent } from './sample-errors-module/run-status/run-status.component';
import { ControlQcsummaryComponent } from './controls-module/control-qcsummary/control-qcsummary.component';
import { ControlFrreportComponent } from './controls-module/control-frreport/control-frreport.component';
import { RunSetupComponent } from './run-module/run-setup/run-setup.component';
import { RunReportItemsComponent } from './run-module/run-report-items/run-report-items.component';

export const routes: Routes = [
    {
        path: 'login',
        loadChildren: () => import('./login-module/login.module').then(m => m.LoginModule)
    },
    {
        title: 'Studies',
        path: 'studies',
        data: { requiresStudy: false },
        loadChildren: () => import('./studies-module/studies.module').then(m => m.StudiesModule),
        canActivate: [authGuard]
    },
    {
        title: 'Run Data',
        path: 'run-data/file-upload',
        data: { requiresStudy: false },
        loadChildren: () => import('./run-data-module/run.data.module').then(m => m.RunDataModule),
        canActivate: [authGuard]
    },
    {
        title: 'Run Info',
        path: 'run',
        data: { requiresStudy: false },
        loadChildren: () => import('./run-module/run.module').then(m => m.RunModule),
        canActivate: [authGuard]
    },
    {
        title: 'Assays',
        path: 'assay-analysis',
        data: { requiresStudy: true },
        loadChildren: () => import('./assay-analysis-module/assay-analysis.module').then(m => m.AssayAnalysisModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Calculations',
        path: 'calculations',
        data: { requiresStudy: true },
        loadChildren: () => import('./calculations-module/calculations.module').then(m => m.CalculationsModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Samples',
        path: 'sample-analysis',
        data: { requiresStudy: true },
        loadChildren: () => import('./sample-analysis-module/sample-analysis.module').then(m => m.SampleAnalysisModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Calibration',
        path: 'calibration',
        data: { requiresStudy: true },
        loadChildren: () => import('./calibration-module/calibration.module').then(m => m.CalibrationModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Controls',
        path: 'controls',
        data: { requiresStudy: true },
        loadChildren: () => import('./controls-module/controls.module').then(m => m.ControlsModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Assign',
        path: 'assignment',
        data: { requiresStudy: true },
        loadChildren: () => import('./assignment-module/assignment.module').then(m => m.AssignmentModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Run Status',
        path: 'sample-errors',
        data: { requiresStudy: true },
        component: RunStatusComponent,
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'C of A',
        path: 'coa',
        data: { requiresStudy: true },
        loadChildren: () => import('./coa-module/coa.module').then(m => m.CoaModule),
        canActivate: [authGuard, stateGuard]
    },
    {
        title: 'Log Out',
        path: 'logout',
        data: { requiresStudy: false },
        loadChildren: () => import('./login-module/login.module').then(m => m.LoginModule)
    },
    {
        path: 'about',
        component: AboutComponent,
        canActivate: [authGuard]
    },
    {
        path: "**",
        redirectTo: '/login'
    },
    // Outside the Print View wrapper: the certificate is a fixed Letter sheet, and the wrapper's
    // header and footer bands would push it onto a second page.
    {
        path: 'print-coa',
        outlet: 'print',
        loadComponent: () => import('./coa-module/coa-preview/coa-preview.component').then(m => m.CoaPreviewComponent),
        canActivate: [authGuard, stateGuard]
    },
    {
        path: 'print',
        outlet: 'print',
        component: PrintViewComponent,
        children: [
            {
                path: 'preview-assay-details', component: AssayDetailsFailuresComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-assay-failures', component: AssayFailuresComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-assay-summary', component: AssaySummaryComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-qc-summary', component: ControlQcsummaryComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-fr-report', component: ControlFrreportComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-run-setup', component: RunSetupComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-run-report-items', component: RunReportItemsComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-audit-trail', component: AuditTrailComponent,
                canActivate: [authGuard, stateGuard, auditTrailPrintGuard]
            },
            {
                path: 'preview-assignment-summary',
                loadChildren: () => import('./assignment-module/assignment-print.routes').then(m => m.assignmentSummaryPrintRoutes),
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-assignment-details',
                loadChildren: () => import('./assignment-module/assignment-print.routes').then(m => m.assignmentDetailsPrintRoutes),
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'preview-sample-errors', component: SampleErrorsComponent,
                canActivate: [authGuard, stateGuard]
            },
            {
                path: 'calibration-assays', component: CalibrationDetailsComponent,
                canActivate: [authGuard, stateGuard]
            }
        ],
        canActivate: [authGuard, stateGuard]
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes)
    ],
    exports: [RouterModule],
})
export class AppRoutingModule { }
